import { useCallback, useEffect, useState, useRef } from 'react';
import { Button, Radio, Select, message } from 'antd';
import DataTable from '../../components/DataTable';
import ItemTabs from './ItemTabs';
import EditScoreModal from './EditScoreModal';
import request from '@/request';

/** Scores：展示赛项成绩列表，并提供已有成绩的修改入口 */
export default function Scores({ event, items }) {
  const dataTableRef = useRef(null);
  const [activeItem, setActiveItem] = useState(items[0] || null);
  const [editScoreVisible, setEditScoreVisible] = useState(false);
  const [selectedScoreRecord, setSelectedScoreRecord] = useState(null);
  const [scorePublishType, setScorePublishType] = useState(0);
  const [scorePublishLoading, setScorePublishLoading] = useState(false);
  const [selectedRound, setSelectedRound] = useState('');
  const [roundOptions, setRoundOptions] = useState([]);

  /** 根据当前赛项回显成绩发布状态，未配置时默认隐藏 */
  useEffect(() => {
    setScorePublishType(Number(activeItem?.publishScore) === 1 ? 1 : 0);
  }, [activeItem]);

  useEffect(() => {
    setSelectedRound('');
    setRoundOptions([]);
  }, [activeItem?.id]);

  /** 根据当前页成绩生成轮次筛选项 */
  const handleScoreDataChange = useCallback((records) => {
    const rounds = Array.from(
      new Set(
        records
          .map((record) => String(record?.part3 ?? '').trim())
          .filter(Boolean)
      )
    ).sort((a, b) => {
      const numberA = Number(a);
      const numberB = Number(b);
      if (Number.isFinite(numberA) && Number.isFinite(numberB)) {
        return numberA - numberB;
      }
      return a.localeCompare(b);
    });

    setRoundOptions(rounds);
    setSelectedRound((currentRound) =>
      currentRound && !rounds.includes(currentRound) ? '' : currentRound
    );
  }, []);

  /** 按轮次筛选当前页成绩 */
  const filterScoreByRound = useCallback(
    (record) => {
      if (!selectedRound) return true;
      return String(record?.part3 ?? '').trim() === selectedRound;
    },
    [selectedRound]
  );

  /** 切换当前赛项的成绩发布状态 */
  const handleScorePublishChange = async (event) => {
    const nextType = event.target.value;
    const previousType = scorePublishType;

    setScorePublishType(nextType);
    setScorePublishLoading(true);

    try {
      await request.post('/events/score/publish', {
        itemId: String(activeItem?.id ?? ''),
        publishScore: nextType,
      });
      message.success(nextType === 1 ? '成绩已展示' : '成绩已隐藏');
    } catch (error) {
      setScorePublishType(previousType);
      message.error(error.message || '成绩发布状态切换失败');
    } finally {
      setScorePublishLoading(false);
    }
  };

  /** 判断选手是否已有成绩，0 分也视为已有成绩 */
  const hasPlayerScore = (record) => {
    return (
      record?.part1 !== undefined &&
      record?.part1 !== null &&
      record?.part1 !== ''
    );
  };

  /** 打开成绩修改弹窗并记录当前选手 */
  const openEditScoreModal = (record) => {
    setSelectedScoreRecord(record);
    setEditScoreVisible(true);
  };

  /** 关闭成绩修改弹窗并清理当前选手 */
  const closeEditScoreModal = () => {
    setEditScoreVisible(false);
    setSelectedScoreRecord(null);
  };

  /** 成绩修改成功后关闭弹窗并刷新列表 */
  const handleEditScoreSuccess = () => {
    closeEditScoreModal();
    dataTableRef.current?.refresh?.();
  };

  /** 在新标签页打开当前赛项的成绩大屏 */
  const handleOpenScoreScreen = () => {
    if (!event?.id || !activeItem?.id) {
      message.warning('请先选择赛项');
      return;
    }

    const url = new URL('/score-screen', window.location.origin);
    url.searchParams.set('eventId', event.id);
    url.searchParams.set('itemId', activeItem.id);

    const scoreScreenWindow = window.open(url.toString(), '_blank');
    if (!scoreScreenWindow) {
      message.error('新标签页打开失败，请检查浏览器弹窗设置');
      return;
    }

    scoreScreenWindow.opener = null;
  };

  const options = [
    { value: 'teamName', label: '机构名称' },
    { value: 'name', label: '选手名称' },
    { value: 'playerId', label: '选手编号' },
  ];

  const columns = [
    { title: '机构名称', dataIndex: 'teamName', key: 'teamName' },
    { title: '选手', dataIndex: 'name', key: 'name' },
    { title: '选手编号', dataIndex: 'playerId', key: 'playerId' },
    {
      title: '轮次',
      dataIndex: 'part3',
      key: 'part3',
      render: (value) => {
        const round = String(value ?? '').trim();
        return round ? `第${round}轮` : '-';
      },
    },
    {
      title: '排名',
      dataIndex: 'detailRank',
      key: 'rank',
      render: (text, record) => {
        if (activeItem?.typeId !== 3 && activeItem?.typeId !== 4) return text;
        return (
          (text || '') + (text && record.rank ? ` - ${record.rank}强` : '')
        );
      },
    },
    { title: '成绩', dataIndex: 'part1', key: 'part1' },
    { title: '用时', dataIndex: 'useTime', key: 'useTime' },
    {
      title: '操作',
      key: 'action',
      render: (_, record) =>
        activeItem?.contestFormat === 1 && hasPlayerScore(record) ? (
          <Button
            type="link"
            style={{ paddingInline: 0 }}
            onClick={() => openEditScoreModal(record)}
          >
            修改成绩
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <ItemTabs
        items={items}
        activeType={activeItem}
        onChange={setActiveItem}
      />
      <DataTable
        ref={dataTableRef}
        options={options}
        onDownload={() => {}}
        onImport={() => {
          if (dataTableRef.current?.refresh) {
            dataTableRef.current.refresh();
          }
        }}
        columns={columns}
        dataSourceFilter={filterScoreByRound}
        onDataSourceChange={handleScoreDataChange}
        queryExtra={
          <div className="flex shrink-0 items-center gap-2">
            <Select
              className="w-30"
              value={selectedRound}
              onChange={setSelectedRound}
              options={[
                { label: '全部轮次', value: '' },
                ...roundOptions.map((round) => ({
                  label: `第${round}轮`,
                  value: round,
                })),
              ]}
            />
            <Button
              type="primary"
              disabled={!event?.id || !activeItem?.id}
              onClick={handleOpenScoreScreen}
            >
              打开成绩大屏
            </Button>
            <Radio.Group
              optionType="button"
              buttonStyle="solid"
              value={scorePublishType}
              disabled={scorePublishLoading || !activeItem?.id}
              onChange={handleScorePublishChange}
              options={[
                { label: '隐藏成绩', value: 0 },
                { label: '显示成绩', value: 1 },
              ]}
            />
          </div>
        }
        api={`/events/admin/import/outer/player/score/list?eventId=${event?.id}&itemId=${activeItem?.id}`}
        importAPI={`/events/admin/import/outer/player/score?eventId=${event?.id}`}
        exportAPI={`/events/admin/schedule/outer/player/score/${activeItem?.id}`}
        demoDownloadUrl="https://statics.dronex.cc/template/EDE%E6%88%90%E7%BB%A9%E6%A8%A1%E7%89%88.xlsx"
      />
      <EditScoreModal
        open={editScoreVisible}
        activeItem={activeItem}
        player={selectedScoreRecord}
        onCancel={closeEditScoreModal}
        onSuccess={handleEditScoreSuccess}
      />
    </div>
  );
}
